/**
 * The scope every item search carries — the enquiry's company and type (Akhil, 2026-09-21).
 *
 * `items.get_items` and `bosun-search /v1/match` both take `company` and `enquiry_type` now
 * (measured 2026-09-21: accepted on both; the ranking does not change for them yet, so this is
 * the wiring the filter lands on). The scope belongs to the ENQUIRY a page is working on, so the
 * page sets it once (`useItemScope`) and the matcher, the catalogue picker and the prefetch all
 * read it — rather than every call site threading two more arguments.
 *
 * Module state, like `company.ts`: the matcher's request functions run outside React.
 */
import { useEffect } from 'react'

export interface ItemScope {
  company: string
  enquiryType: string
}

/*
  ---- The demo stand-in: a SETTING, not a constant in the code ----

  Most enquiries on the bench carry no company (an auto-created one never does, by design: the
  RFQ header asks for it), so every item search went out unscoped and the company filter could
  not be shown working. `VITE_BOSUN_DEMO_COMPANY` stands in for a blank company where it is set
  — the local `.env` for a demo — and is EMPTY everywhere else, so a deployed build never
  carries one (Akhil, 2026-09-23: a stand-in company is a wrong fact about an enquiry the moment
  two companies are in play). A real company on the header is never overridden.
*/
const DEMO_COMPANY = String(import.meta.env.VITE_BOSUN_DEMO_COMPANY ?? '')

/** The company an item search is scoped to: the page's own, or the demo stand-in for a blank. */
export function scopeCompany(company: string, fallback: string): string {
  return company.trim() || fallback.trim()
}

let current: ItemScope = { company: '', enquiryType: '' }

export function setItemScope(scope: ItemScope): void {
  // `DEMO_COMPANY` only where the page knows none — see the note above it.
  current = { company: scopeCompany(scope.company, DEMO_COMPANY), enquiryType: scope.enquiryType.trim() }
}

export function getItemScope(): ItemScope {
  return current
}

/** The two query parameters, only where known. */
export function itemScopeParams(): { company?: string; enquiry_type?: string } {
  return {
    ...(current.company ? { company: current.company } : {}),
    ...(current.enquiryType ? { enquiry_type: current.enquiryType } : {}),
  }
}

/** A page declares the enquiry its searches are for. */
export function useItemScope(scope: ItemScope): void {
  useEffect(() => {
    setItemScope(scope)
  }, [scope.company, scope.enquiryType])
}

export function __resetItemScopeForTests(): void {
  current = { company: '', enquiryType: '' }
}


export interface ItemSearchPlan {
  params: Record<string, string | number>
  /** The same search without the type, for when the type finds nothing; `null` when none was used. */
  fallback: Record<string, string | number> | null
}

/**
 * What one catalogue search sends, and what to try when it comes back empty.
 *
 * The enquiry type narrows `items.get_items` through a mapping on `Enquiry Type` that is barely
 * configured — and, measured 2026-09-22, is now gone entirely: every non-empty `enquiry_type`
 * answers ZERO rows (`D45`). So a search that used a type and found nothing runs again without
 * it: scoped where the mapping works, a working search everywhere else.
 *
 * The type is taken from the caller first, then the page's scope. It used to matter which —
 * only a caller's type got the fallback — so a picker on an enquiry with a type simply found
 * nothing. One plan for both.
 */
export function planItemSearch(
  query: { q?: string; enquiry_type?: string; limit?: number; offset?: number },
  scope: ItemScope,
): ItemSearchPlan {
  const type = (query.enquiry_type ?? scope.enquiryType).trim()
  const common: Record<string, string | number> = {
    ...(query.q !== undefined ? { q: query.q } : {}),
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    ...(scope.company ? { company: scope.company } : {}),
  }
  return {
    params: type ? { ...common, enquiry_type: type } : common,
    fallback: type ? common : null,
  }
}
